import { RuleConditions } from '~/consts/agentHub';

import type { Commission } from '~/types/services/agentHub';

import { additionalLocationToCommissionOption } from '~/utils/commission';

/** API / list shape for departure & arrival extras (often alongside flight rules). */
export type FlightLocationAdditionalInfo = {
  departure?: { city?: string; country?: string };
  arrival?: { city?: string; country?: string };
};

/**
 * Reads `additionalInfo` from a commission when present (contract types may lag the API).
 * Single place for the cast so components stay simple.
 */
export function readCommissionAdditionalInfo(commission: Commission | undefined): Record<string, unknown> | undefined {
  if (!commission || typeof commission !== 'object') {
    return undefined;
  }
  if (!('additionalInfo' in commission)) {
    return undefined;
  }
  const info = (commission as { additionalInfo?: unknown }).additionalInfo;
  if (info && typeof info === 'object' && !Array.isArray(info)) {
    return info as Record<string, unknown>;
  }
  return undefined;
}

export function getFlightLocationAdditionalInfo(
  commission: Commission | undefined,
): FlightLocationAdditionalInfo | undefined {
  const raw = readCommissionAdditionalInfo(commission);
  if (!raw) {
    return undefined;
  }
  const out: FlightLocationAdditionalInfo = {};
  if (raw.departure && typeof raw.departure === 'object' && !Array.isArray(raw.departure)) {
    out.departure = raw.departure as FlightLocationAdditionalInfo['departure'];
  }
  if (raw.arrival && typeof raw.arrival === 'object' && !Array.isArray(raw.arrival)) {
    out.arrival = raw.arrival as FlightLocationAdditionalInfo['arrival'];
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Maps legacy API rule keys to form `RuleConditions` keys.
 */
export function normalizeApiRulesForCommissionForm(rawRules: Record<string, unknown>): Record<string, unknown> {
  const next = { ...rawRules };
  if (next.carrier != null && next.flightCarrier == null) {
    next.flightCarrier = next.carrier;
  }
  if (next.departure != null && next.flightDeparture == null) {
    next.flightDeparture = next.departure;
  }
  if (next.arrival != null && next.flightArrival == null) {
    next.flightArrival = next.arrival;
  }
  delete next.carrier;
  delete next.departure;
  delete next.arrival;
  return next;
}

/**
 * After building `RuleConditions` from `rules`, overlay flight departure/arrival from `additionalInfo` when present.
 */
export function overlayFlightLocationsFromAdditionalInfo(
  conditions: RuleConditions,
  additionalInfo: unknown,
): RuleConditions {
  if (!additionalInfo || typeof additionalInfo !== 'object' || Array.isArray(additionalInfo)) {
    return conditions;
  }
  const info = additionalInfo as FlightLocationAdditionalInfo;
  let next = conditions;
  if (info.departure && typeof info.departure === 'object') {
    const opt = additionalLocationToCommissionOption(info.departure);
    if (opt.length > 0) {
      next = { ...next, flightDeparture: opt };
    }
  }
  if (info.arrival && typeof info.arrival === 'object') {
    const opt = additionalLocationToCommissionOption(info.arrival);
    if (opt.length > 0) {
      next = { ...next, flightArrival: opt };
    }
  }
  return next;
}
